import React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva } from 'class-variance-authority';
import cn from './cn';

/**
 * shadcn's Button, with the CMS palette and the CMS proportions.
 *
 * The variants are not shadcn's defaults. `default` reproduces what `.btn` renders today
 * (brand fill, white text, 7px/16px padding, 5px radius, 14px/19px type) so a converted
 * button lands in the same place at the same size. What it adds is the part the
 * stylesheet never had: a real hover, a visible focus ring, and a disabled state that is
 * not just a greyed cursor.
 *
 * `asChild` is Radix's Slot. It lets a caller render a link or a label with button
 * styling without nesting an `<a>` inside a `<button>`, which the file-picker labels in
 * pdf-manager currently fake with `className="btn btn-success"`.
 */
/**
 * A disabled button (redesign iteration 1, states.md). Never an opacity: an opacity of a
 * token is a colour no token names, and a faded brand button read as a paler brand rather
 * than as off. A button with a surface keeps its shape on --fill with the --border-light
 * edge and --subtle ink; a button with no surface (link, ghost) only loses its ink.
 */
const DISABLED_SURFACE =
  'disabled:tw-bg-fill disabled:tw-border-line-light disabled:tw-text-subtle disabled:tw-shadow-none';
const DISABLED_INK = 'disabled:tw-text-subtle';

/**
 * The same off look, for a button that says why it is off (SURF-4889). Such a button is
 * `aria-disabled` rather than `disabled` (shared/ui/Button's `disabledReason`): it keeps its
 * focus and its pointer events so its tooltip can open, so the `disabled:` paint above never
 * reaches it. These are that paint without the prefix, plus the hover the variant would
 * otherwise show through and a not-allowed cursor. Written out in full rather than derived
 * from the strings above, because Tailwind only builds class names it can read in the source.
 */
export const UNAVAILABLE_SURFACE =
  'tw-bg-fill tw-border-line-light tw-text-subtle tw-shadow-none hover:tw-bg-fill hover:tw-text-subtle tw-cursor-not-allowed';
export const UNAVAILABLE_INK = 'tw-text-subtle hover:tw-text-subtle tw-cursor-not-allowed';

export const buttonVariants = cva(
  [
    'tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-whitespace-nowrap',
    // `normal-case` is declared, not assumed. The tenant's own `wysiwygPageStyles.css` is
    // linked into the CMS document after sign-in, before the bundle's stylesheet
    // (`router/tenantStylesheets.js`). A bare `button { text-transform: uppercase }` there
    // (an ordinary thing to write for app content) ties main.css's base rule and loses on
    // order, but one step more specific (`body button`) beats the base rule and every
    // button in the CMS comes out shouting.
    // Tailwind is configured `important: true`, so this utility holds against either.
    'tw-normal-case',
    'tw-rounded-control tw-font-sans tw-text-body tw-align-middle',
    'tw-border tw-border-solid tw-border-transparent',
    'tw-transition-colors tw-duration-150',
    'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-brand-focus',
    'focus-visible:tw-ring-offset-1',
    'disabled:tw-pointer-events-none',
    '[&>i]:tw-text-[0.9em]',
  ],
  {
    variants: {
      variant: {
        /** The CMS default: every toolbar button is this today. */
        // Secondary is the CMS default. A toolbar should carry one primary; filling
        // every button with the brand colour said everything was the main action.
        default: cn(
          'tw-bg-white tw-text-ink-2 tw-border tw-border-line tw-shadow-sm hover:tw-bg-off-white',
          DISABLED_SURFACE
        ),
        primary: cn(
          'tw-bg-brand tw-text-white tw-shadow-sm hover:tw-bg-brand-hover',
          DISABLED_SURFACE
        ),
        /** Cancel and Close. Transparent with a hairline, not a second filled button. */
        secondary: cn(
          'tw-bg-transparent tw-text-ink tw-border-line tw-shadow-sm hover:tw-bg-fill hover:tw-text-ink',
          DISABLED_SURFACE
        ),
        success: cn(
          'tw-bg-success-dot tw-text-white tw-shadow-sm hover:tw-bg-success',
          DISABLED_SURFACE
        ),
        danger: cn(
          'tw-bg-danger tw-text-white tw-shadow-sm hover:tw-bg-danger-strong',
          DISABLED_SURFACE
        ),
        /** Looks like a link, behaves like a button. */
        link: cn(
          'tw-bg-transparent tw-text-brand-link tw-underline tw-border-transparent hover:tw-text-brand',
          DISABLED_INK
        ),
        /**
         * No surface until it is pointed at. For controls that sit in a run of their own
         * kind, where a border on every one would be noise: the page numbers in a pager.
         */
        ghost: cn(
          'tw-bg-transparent tw-text-ink-2 tw-border-transparent tw-shadow-none hover:tw-bg-fill hover:tw-text-ink',
          DISABLED_INK
        ),
      },
      size: {
        // `tw-py-0` because the tenant's stylesheet, loaded into the CMS, pads every bare
        // `button` 10px top and bottom; the height is what sizes these.
        default: 'tw-h-control tw-px-control-x tw-py-0',
        sm: 'tw-px-3 tw-py-1 tw-text-small',
        xs: 'tw-px-2 tw-py-0.5 tw-text-caption',
        icon: 'tw-h-8 tw-w-8 tw-p-0',
        /** A pager button: square for a page number, wider for Previous and Next. */
        page: 'tw-h-8 tw-min-w-8 tw-px-2 tw-py-0 tw-gap-1',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
);

const Button = React.forwardRef(
  ({ className, variant, size, asChild = false, type = 'button', ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        // Slot forwards to whatever the caller rendered, which may not take `type`.
        {...(asChild ? {} : { type })}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  }
);
Button.displayName = 'Button';

export default Button;
